import { Stack, Typography, Box, Skeleton } from '@mui/material';
import BigNumber from 'bignumber.js';
import { OLstvToken, ONativeToken } from 'modules/api';
import { SpriteIcon } from 'modules/common/components/SpriteIcon';
import { lbtcSpriteIcon } from 'modules/common/icons';
import { useGlobalTranslation, useTranslation } from 'modules/i18n';

import { translation } from './translation';
import { useShareValue } from 'modules/withdrawal/hooks/useShareValue';
import { formatBtc } from 'modules/common/utils/formatBtc';

type WithdrawalInfoProps = { amount: string };

export const WithdrawalInfo = ({ amount }: WithdrawalInfoProps) => {
  const { t, keys: globalKeys } = useGlobalTranslation();
  const { keys } = useTranslation(translation);
  const amountValue = BigNumber(amount || 0);
  const { data: shareValue, isLoading: isShareValueLoading } = useShareValue();

  const exchangeRate = shareValue || BigNumber(0);

  if (isShareValueLoading) {
    return (
      <Stack gap={0} direction="column">
        <Skeleton
          sx={{
            width: '100%',
            height: '28px',
          }}
        />
        <Skeleton
          sx={{
            width: '100%',
            height: '28px',
          }}
        />
        <Skeleton
          sx={{
            width: '100%',
            height: '28px',
          }}
        />
      </Stack>
    );
  }

  return (
    <Stack gap={1}>
      {/* Exchange rate */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          color="text.secondary"
          fontSize={{
            xs: 14,
            sm: 16,
          }}
        >
          {t(keys.exchangeRate)}
        </Typography>

        <Stack direction="row" alignItems="center" gap={1}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography
              fontSize={{
                xs: 14,
                sm: 16,
              }}
              textAlign="right"
            >
              {t(globalKeys.unit.tokenValue, {
                token: OLstvToken.sonicLBTCv,
                value: BigNumber(1),
              })}
            </Typography>
            <Box
              component={SpriteIcon}
              src={lbtcSpriteIcon}
              height={20}
              minWidth={20}
            />
          </Stack>

          {t(globalKeys.general.equals)}
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography
              fontSize={{
                xs: 14,
                sm: 16,
              }}
              textAlign="right"
            >
              {t(globalKeys.unit.tokenValue, {
                token: ONativeToken.LBTC,
                value: exchangeRate.toFormat(4, BigNumber.ROUND_DOWN),
              })}
            </Typography>
            <Box
              component={SpriteIcon}
              src={lbtcSpriteIcon}
              height={20}
              minWidth={20}
            />
          </Stack>
        </Stack>
      </Stack>

      {/* You withdraw */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          color="text.secondary"
          fontSize={{
            xs: 14,
            sm: 16,
          }}
        >
          {t(keys.youWithdraw)}
        </Typography>

        <Stack direction="row" alignItems="center" gap={1}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography
              fontSize={{
                xs: 14,
                sm: 16,
              }}
            >
              {t(globalKeys.unit.tokenValue, {
                token: OLstvToken.sonicLBTCv,
                value: formatBtc(BigNumber(amount || 0)),
              })}
            </Typography>

            <Box
              component={SpriteIcon}
              src={lbtcSpriteIcon}
              height={20}
              width={20}
            />
          </Stack>
        </Stack>
      </Stack>

      {/* You receive */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          color="text.secondary"
          fontSize={{
            xs: 14,
            sm: 16,
          }}
        >
          {t(keys.youReceive)}
        </Typography>

        <Stack direction="row" alignItems="center" gap={1}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography
              fontSize={{
                xs: 14,
                sm: 16,
              }}
            >
              {t(globalKeys.unit.tokenValue, {
                token: ONativeToken.LBTC,
                value: formatBtc(amountValue.multipliedBy(exchangeRate)),
              })}
            </Typography>
            <Box
              component={SpriteIcon}
              src={lbtcSpriteIcon}
              height={20}
              width={20}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
